import DOMPurify from "dompurify";
import {
  initMenu,
  initSearchInput,
  initTabList
} from "../../index.js";
class AppSwitcher {
  constructor(appSwitcher, props) {
    this.#apps = [];
    this.#menu = null;
    this.#menuTitleText = null;
    this.#chevronIcon = null;
    this.#layout = null;
    this.#toolbar = null;
    this.#layoutToggle = null;
    this.#tabList = null;
    this.#appsContainer = null;
    this.#noAppsContainer = null;
    this.#subAppsHeader = null;
    this.#subAppsHeaderBackBtn = null;
    this.#subAppsContainer = null;
    this.#subAppsHeaderParentTitle = null;
    this.#searchInputContainer = null;
    this.#searchInput = null;
    this.#searchResults = null;
    this.#rightArrowIconTemplate = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" class="tds-icon tds-icon--small tds-icon-arrow-small-90">
        <path fill="currentColor" d="M8.47 12.78a.747.747 0 0 1 0-1.06l2.97-2.973-8.69.003a.75.75 0 0 1 0-1.5l8.69-.003L8.477 4.28a.75.75 0 1 1 1.06-1.06l4.243 4.247a.75.75 0 0 1 0 1.06L9.53 12.78a.75.75 0 0 1-1.06 0z"/>
      </svg>
    `;
    // Visual manager for each possible layout within app switcher
    this.#setLayout = (layout) => {
      if (this.#apps.length === 0) {
        layout = "noapps";
      }
      const hiddenClass = "tds--is_hidden";
      switch (layout) {
        case "apps":
          this.#subAppsHeader?.classList.add(hiddenClass);
          this.#subAppsContainer?.classList.add(hiddenClass);
          this.#searchResults?.classList.add(hiddenClass);
          this.#noAppsContainer?.classList.add(hiddenClass);
          this.#toolbar?.classList.remove(hiddenClass);
          this.#appsContainer?.classList.remove(hiddenClass);
          this.#layoutToggle?.classList.remove(hiddenClass);
          break;
        case "noapps":
          this.#appsContainer?.classList.add(hiddenClass);
          this.#subAppsHeader?.classList.add(hiddenClass);
          this.#subAppsContainer?.classList.add(hiddenClass);
          this.#searchResults?.classList.add(hiddenClass);
          this.#toolbar?.classList.remove(hiddenClass);
          this.#noAppsContainer?.classList.remove(hiddenClass);
          break;
        case "subapps":
          this.#toolbar?.classList.add(hiddenClass);
          this.#appsContainer?.classList.add(hiddenClass);
          this.#searchResults?.classList.add(hiddenClass);
          this.#noAppsContainer?.classList.add(hiddenClass);
          this.#subAppsHeader?.classList.remove(hiddenClass);
          this.#subAppsContainer?.classList.remove(hiddenClass);
          break;
        case "search":
          this.#layoutToggle?.classList.add(hiddenClass);
          this.#appsContainer?.classList.add(hiddenClass);
          this.#subAppsHeader?.classList.add(hiddenClass);
          this.#subAppsContainer?.classList.add(hiddenClass);
          this.#noAppsContainer?.classList.add(hiddenClass);
          this.#toolbar?.classList.remove(hiddenClass);
          this.#searchResults?.classList.remove(hiddenClass);
          break;
        default:
          break;
      }
      this.#layout?.classList.remove(hiddenClass);
    };
    this.#appSearch = (apps, searchValue) => {
      const results = [];
      const queryParts = searchValue.toLowerCase().split(" ");
      apps?.forEach((app) => {
        const appTitle = app.title.toLowerCase();
        if (appTitle.includes(queryParts[0])) {
          const result = { href: app.href, subApps: [], title: app.title };
          if (queryParts.length > 1 && queryParts[1].trim() !== "" && app.subApps) {
            app.subApps.forEach((subApp) => {
              const subAppTitle = subApp.title.toLowerCase();
              if (subAppTitle.includes(queryParts[1]) && result.subApps) {
                result.subApps.push({ href: subApp.href, subApps: [], title: subApp.title });
              }
            });
          } else {
            result.subApps = app.subApps;
          }
          results.push(result);
        }
      });
      return results;
    };
    this.#renderAppSearchResults = (appSearchResults) => {
      if (this.#searchResults)
        this.#searchResults.innerHTML = "";
      appSearchResults?.forEach((result, index) => {
        const { href, subApps, title: parentAppTitle } = result;
        if (subApps && subApps.length > 0) {
          const parentTitleTemplate = `
          <div class="tds-app-switcher-search-result-title tds-text--caption">${parentAppTitle}</div>
        `;
          const subAppsTemplate = `
            ${subApps.map((subApp) => {
            const searchResultTemplate = `
                  <a class="tds-app-switcher-search-result" href="${subApp.href}">
                    <span>${subApp.title}</span>
                    ${this.#rightArrowIconTemplate}
                  </a>
                `;
            return searchResultTemplate;
          }).join("")}
              ${// If it's the last result add a divider at the end
          index !== appSearchResults.length - 1 ? '<div class="tds-app-switcher-search-result-divider"></div>' : ""}
        `;
          this.#searchResults?.insertAdjacentHTML(
            "beforeend",
            DOMPurify.sanitize(parentTitleTemplate)
          );
          this.#searchResults?.insertAdjacentHTML(
            "beforeend",
            DOMPurify.sanitize(subAppsTemplate, {
              ADD_TAGS: ["use"]
            })
          );
        } else {
          const template = `
            <a class="tds-app-switcher-search-result" href=${href}>
              <span>${parentAppTitle}</span>
              ${this.#rightArrowIconTemplate}
            </a>
            ${// If the next result contains subapps then add a divider
          appSearchResults?.[index + 1]?.subApps?.length ? '<div class="tds-app-switcher-search-result-divider"></div>' : ""}
        `;
          this.#searchResults?.insertAdjacentHTML(
            "beforeend",
            DOMPurify.sanitize(template, {
              ADD_TAGS: ["use"]
            })
          );
        }
      });
    };
    this.#handleAppOnClick = (e) => {
      const appRef = e?.target;
      const appIndex = Number(appRef?.dataset?.appIndex);
      const app = this.apps[appIndex];
      if (app?.subApps && app?.subApps?.length > 0 && this.#appsContainer) {
        this.#initSubApps(app);
      } else {
        if (app?.openNewTab) {
          window.open(app.href, "_blank");
        } else {
          window.location.href = app.href;
        }
      }
    };
    this.#renderAppSelection = (apps) => {
      if (this.#appsContainer) {
        const appItems = this.#appsContainer.querySelectorAll(".tds-app-switcher-apps-item");
        appItems.forEach((appItem) => {
          appItem.removeEventListener("click", this.#handleAppOnClick);
        });
        this.#appsContainer.innerHTML = "";
      } else {
        return;
      }
      apps.forEach((app, index) => {
        const tag = app?.subApps?.length ? "button" : app?.href ? "a" : "div";
        const template = `
         <${tag} class="tds-app-switcher-apps-item" id="${app?.title}" title="${app?.title}" data-app-index="${index}" tabindex="0">
              <div class="tds-app-switcher-apps-item-logo ${app?.img ? `tds-app-switcher-apps-item-logo--has-image` : ``}">
              ${app?.img ? `<img src="${app?.img}" alt="" />` : ""}
              </div>

           <div class="tds-app-switcher-apps-item-title">${app?.title}</div>
         </${tag}>
       `;
        this.#appsContainer?.insertAdjacentHTML("beforeend", DOMPurify.sanitize(template));
        const appRef = this.#appsContainer?.querySelector(`[data-app-index="${index}"]`);
        appRef?.addEventListener("click", this.#handleAppOnClick);
      });
    };
    this.#renderSubAppsParentTitle = (app) => {
      const { title: parentTitle } = app;
      if (this.#subAppsHeaderParentTitle)
        this.#subAppsHeaderParentTitle.textContent = parentTitle;
    };
    this.#renderSubApps = (subApps) => {
      if (this.#subAppsContainer)
        this.#subAppsContainer.innerHTML = "";
      subApps.forEach((app) => {
        const subAppsContainerItemTemplate = `
        <a class="tds-app-switcher-subapps-item" href="${app.href}" tabindex="0">
          <span title="${app.title}">${app.title}</span>
          ${this.#rightArrowIconTemplate}
        </a>
      `;
        this.#subAppsContainer?.insertAdjacentHTML(
          "beforeend",
          DOMPurify.sanitize(subAppsContainerItemTemplate, {
            ADD_TAGS: ["use"]
          })
        );
      });
    };
    this.#initAppSwitcher = () => {
      this.#initBoxShadowScrollListeners();
      this.#initMenu();
      this.#initToggle();
      if (this.apps) {
        this.#initApps(this.apps);
        this.#initSearch(this.apps);
      }
    };
    // Init menu functionality, including the flipping of the chevron icon
    this.#initMenu = () => {
      if (this.#menuTitleText && this.appTitle) {
        let menuTitle;
        if (this.subAppTitle) {
          menuTitle = `<strong>${this.appTitle}</strong> ${this.subAppTitle}`;
        } else {
          menuTitle = this.appTitle;
        }
        this.#menuTitleText.innerHTML = DOMPurify.sanitize(menuTitle);
        this.#menuTitleText.setAttribute("title", menuTitle);
      }
      const onAppSwitcherOpen = () => {
        this.#chevronIcon?.classList.add("tds-icon--flip-x");
      };
      const onAppSwitcherClose = () => {
        this.#chevronIcon?.classList.remove("tds-icon--flip-x");
      };
      const menuProps = {
        onClose: onAppSwitcherClose,
        onOpen: onAppSwitcherOpen
      };
      if (this.#menu)
        initMenu(this.#menu, menuProps);
    };
    // Init grid/list toggle functionality
    this.#initToggle = () => {
      const onTabChange = (tab) => {
        if (tab?.id === "grid") {
          this.#appsContainer?.classList.remove("tds-app-switcher-apps--list");
        }
        if (tab?.id === "list") {
          this.#appsContainer?.classList.add("tds-app-switcher-apps--list");
        }
      };
      const tabListProps = {
        onTabChange
      };
      if (this.#tabList)
        initTabList(this.#tabList, tabListProps);
    };
    // Init app switcher search functionality
    this.#initSearch = (apps) => {
      if (!this.#searchInputContainer)
        return;
      const onSearchValueChange = (searchValue) => {
        if (searchValue && searchValue.trim().length > 0) {
          this.#setLayout("search");
          let appSearchResults = [];
          if (this.onSearch) {
            appSearchResults = this.onSearch(searchValue);
          } else {
            appSearchResults = this.#appSearch(apps, searchValue);
          }
          this.#renderAppSearchResults(appSearchResults);
        } else {
          this.#setLayout("apps");
        }
      };
      const searchInputProps2 = {
        onValueChange: onSearchValueChange
      };
      const handleKeyDown = (e) => {
        switch (e.key) {
          case "Escape":
            if (this.#searchInput?.value) {
              e.stopPropagation();
            }
            break;
        }
      };
      if (this.#searchInput) {
        this.#searchInput?.addEventListener("keydown", handleKeyDown);
        if (this.searchPlaceholder) {
          this.#searchInput.setAttribute("placeholder", this.searchPlaceholder);
        }
      }
      initSearchInput(this.#searchInputContainer, searchInputProps2);
    };
    // Init the subapps to the subapps container
    this.#initSubApps = (app) => {
      this.#setLayout("subapps");
      const { subApps } = app;
      const goBackToApps = () => {
        this.#setLayout("apps");
        this.#subAppsHeaderBackBtn?.removeEventListener("click", goBackToApps);
      };
      this.#subAppsHeaderBackBtn?.addEventListener("click", goBackToApps);
      this.#renderSubAppsParentTitle(app);
      if (subApps)
        this.#renderSubApps(subApps);
    };
    // Init the apps to the container
    this.#initApps = (apps) => {
      if (apps?.length === 0) {
        this.#setLayout("noapps");
      } else {
        this.#setLayout("apps");
        this.#renderAppSelection(apps);
      }
    };
    // Clear out all rendered HTML that relates to apps
    this.#clearApps = () => {
      if (this.#appsContainer)
        this.#appsContainer.innerHTML = "";
      if (this.#subAppsContainer)
        this.#subAppsContainer.innerHTML = "";
      if (this.#searchResults)
        this.#searchResults.innerHTML = "";
    };
    if (appSwitcher) {
      this.#setElements(appSwitcher);
      this.#setProps(props);
      this.#initAppSwitcher();
    } else {
      console.warn("TDS AppSwitcher is missing a reference for `appSwitcher`.");
    }
  }
  #apps;
  set apps(apps) {
    this.#clearApps();
    this.#renderAppSelection(apps);
    this.#apps = apps;
  }
  get apps() {
    return this.#apps;
  }
  #menu;
  #menuTitleText;
  #chevronIcon;
  #layout;
  #toolbar;
  #layoutToggle;
  #tabList;
  #appsContainer;
  #noAppsContainer;
  #subAppsHeader;
  #subAppsHeaderBackBtn;
  #subAppsContainer;
  #subAppsHeaderParentTitle;
  #searchInputContainer;
  #searchInput;
  #searchResults;
  #rightArrowIconTemplate;
  // Set all custom props provided by the user to the class members for easy access
  #setProps(props) {
    this.#apps = props?.apps ?? [];
    this.appTitle = props?.appTitle;
    this.subAppTitle = props?.subAppTitle;
    this.searchPlaceholder = props?.searchPlaceholder;
    this.onSearch = props?.onSearch;
  }
  // Set all of the element references needed
  #setElements(appSwitcher) {
    this.#menu = appSwitcher.querySelector(".tds-menu");
    this.#menuTitleText = appSwitcher.querySelector(".tds-app-title-text");
    this.#chevronIcon = appSwitcher.querySelector(".tds-menu-btn .tds-icon");
    this.#layout = appSwitcher.querySelector(".tds-app-switcher-layout");
    this.#appsContainer = appSwitcher.querySelector(".tds-app-switcher-apps");
    this.#noAppsContainer = appSwitcher.querySelector(".tds-app-switcher-noapps");
    this.#subAppsContainer = appSwitcher.querySelector(".tds-app-switcher-subapps");
    this.#subAppsHeaderBackBtn = appSwitcher.querySelector(".tds-app-switcher-subapps-back");
    this.#subAppsHeaderParentTitle = appSwitcher.querySelector(".tds-app-switcher-subapps-title");
    this.#toolbar = appSwitcher.querySelector(".tds-app-switcher-toolbar");
    this.#layoutToggle = appSwitcher.querySelector(".tds-app-switcher-toggle");
    this.#tabList = appSwitcher.querySelector(".tds-tab-list");
    this.#subAppsHeader = appSwitcher.querySelector(".tds-app-switcher-subapps-header");
    this.#searchInputContainer = appSwitcher.querySelector(".tds-form-input");
    this.#searchInput = appSwitcher.querySelector(".tds-form-input input");
    this.#searchResults = appSwitcher.querySelector(".tds-app-switcher-search-results");
  }
  #setLayout;
  #appSearch;
  #renderAppSearchResults;
  #handleAppOnClick;
  #renderAppSelection;
  #renderSubAppsParentTitle;
  #renderSubApps;
  #initAppSwitcher;
  /*
    Initialize listeners to apply box shadow to the scrollAffectedElement based on the scroll position of the scrollableArea. 
    A box shadow is added to the scrollAffectedElement when the scrollableArea's scroll position is greater than 0.
  */
  #initBoxShadowScrollListener(scrollableArea, scrollAffectedElement) {
    const handleScroll = (e) => {
      const scrollPosition = e.target.scrollTop;
      if (scrollPosition > 0) {
        scrollAffectedElement?.classList.add("tds-app-switcher-scroll-shadow");
      } else {
        scrollAffectedElement?.classList.remove("tds-app-switcher-scroll-shadow");
      }
    };
    scrollableArea?.addEventListener("scroll", handleScroll);
  }
  #initBoxShadowScrollListeners() {
    this.#initBoxShadowScrollListener(this.#appsContainer, this.#toolbar);
    this.#initBoxShadowScrollListener(this.#subAppsContainer, this.#subAppsContainer);
    this.#initBoxShadowScrollListener(this.#searchResults, this.#toolbar);
  }
  #initMenu;
  #initToggle;
  #initSearch;
  #initSubApps;
  #initApps;
  #clearApps;
}
const createAppSwitcher = (appSwitcher, appSwitcherProps) => {
  return new AppSwitcher(appSwitcher, appSwitcherProps);
};
export {
  createAppSwitcher
};
